import { romBaseName } from "./saves";

// Battery saves something replaced or set aside: the copy a cloud download,
// an imported file or a restore wrote over, a deleted save, and a signed-out
// save an account adopted where it already had one (profiles.ts). Nothing
// here is ever deleted; restoring one moves it back into the game and keeps
// the save it replaces here in its place. The database is per account
// (SOURCES in profiles.ts), so it moves aside with the rest of the saves.

export const KEPT_DB = "pocket-play-kept-saves";
export const KEPT_STORE = "saves";

export type KeptSource = "signed-out" | "cloud" | "import" | "deleted" | "restore";

export const KEPT_SOURCE_LABEL: Record<KeptSource, string> = {
  "signed-out": "From before you signed in",
  cloud: "Replaced by the cloud's copy",
  import: "Replaced by an imported file",
  deleted: "Deleted",
  restore: "Replaced by a restore",
};

export interface KeptSave {
  id: string;
  /** The game's battery save name, `<base>.sav`, whichever engine wrote it. */
  file: string;
  source: KeptSource;
  /** Epoch ms it was kept. */
  keptAt: number;
  /** Epoch ms the save itself was last written, when known. */
  savedAt: number | null;
  bytes: Uint8Array;
}

type Stored = Omit<KeptSave, "id">;

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(KEPT_DB);
    request.onupgradeneeded = () => request.result.createObjectStore(KEPT_STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

const settled = <T>(request: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

async function withStore<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => Promise<T>): Promise<T> {
  const db = await open();
  try {
    const tx = db.transaction(KEPT_STORE, mode);
    const done = new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onabort = tx.onerror = () => reject(tx.error);
    });
    const result = await work(tx.objectStore(KEPT_STORE));
    await done;
    return result;
  } finally {
    db.close();
  }
}

let sequence = 0;

export const sameBytes = (a: Uint8Array, b: Uint8Array) => a.length === b.length && a.every((byte, i) => byte === b[i]);

/** Every kept save, newest first. */
export function listKept(): Promise<KeptSave[]> {
  return withStore("readonly", async (store) => {
    const [keys, values] = await Promise.all([
      settled(store.getAllKeys()),
      settled(store.getAll() as IDBRequest<Stored[]>),
    ]);
    return keys
      .map((key, i) => ({ ...values[i], id: String(key) }))
      .sort((a, b) => b.keptAt - a.keptAt || b.id.localeCompare(a.id));
  });
}

/** The kept saves of one game (any of its file names: they pair by base name). */
export const keptFor = (kept: KeptSave[], game: string) =>
  kept.filter((save) => romBaseName(save.file) === romBaseName(game));

/**
 * Keeps a copy of a game's battery save. An empty one, or one already kept
 * with the same bytes, adds nothing.
 */
export async function keepSave(
  game: string,
  bytes: Uint8Array | null | undefined,
  source: KeptSource,
  savedAt: number | null = null,
) {
  if (!bytes?.length) return;
  const file = `${romBaseName(game)}.sav`;
  const kept = keptFor(await listKept(), file);
  if (kept.some((save) => sameBytes(save.bytes, bytes))) return;
  const keptAt = Date.now();
  // Keys sort as they were kept, within a millisecond too; the random part
  // keeps two tabs from taking the same one.
  const id = `${String(keptAt).padStart(15, "0")}-${String(++sequence).padStart(6, "0")}-${Math.random().toString(36).slice(2, 10)}`;
  const value: Stored = { file, source, keptAt, savedAt, bytes: bytes.slice() };
  await withStore("readwrite", async (store) => void store.put(value, id));
}

/** Takes a kept save out of the list once it is the game's save again. */
export function takeKept(id: string): Promise<void> {
  return withStore("readwrite", async (store) => void store.delete(id));
}
